import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/Slice/authSlice";
import { getAlerts, markAlertsRead, readAlertStream } from "../services/alerts";
import { playAlertSound, unlockAlertSound } from "../utils/alertSound";
import { currentPushSubscription, enablePush, notificationPermission, notificationsSupported, permissionAnswer, turnOnNotifications } from "../utils/pushNotifications";
import { toast } from "../utils/toast";
import { ordersChanged } from "../utils/ordersChanged";
import Modal from "./Modal";
import Loader from "./Loader";

// Alerts for the signed-in user (new orders, new websites): loaded once, then kept up to date over a live stream.
// Each new alert plays a chime and shows a toast; when the panel is in the background it also shows a notification:
// a push notification from the service worker where push is on (installed app, or the browser with the panel
// closed), otherwise a plain browser notification.
const AlertsContext = createContext(null);
export const useAlerts = () => useContext(AlertsContext);

const RETRY_MS = [2000, 5000, 10000, 30000];

export function AlertsProvider({ children }) {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(null); // null until loaded
  const [unread, setUnread] = useState(0);
  const [permission, setPermission] = useState(notificationPermission);
  const [pushOn, setPushOn] = useState(false);
  const pushOnRef = useRef(false);
  pushOnRef.current = pushOn;

  const load = useCallback(() => getAlerts().then((data) => { setAlerts(data.alerts); setUnread(data.unread); }).catch(() => {}), []);

  // Opening an alert marks it read and goes to its page (e.g. that order in Manage orders)
  const open = useCallback((alert) => {
    if (!alert.read) {
      setAlerts((list) => (list || []).map((a) => (a.id === alert.id ? { ...a, read: true } : a)));
      markAlertsRead([alert.id]).then((data) => setUnread(data.unread)).catch(() => {});
    }
    if (alert.link) navigate(alert.link);
  }, [navigate]);

  const markAllRead = useCallback(() => {
    setAlerts((list) => (list || []).map((a) => ({ ...a, read: true })));
    setUnread(0);
    markAlertsRead().then((data) => setUnread(data.unread)).catch(() => {});
  }, []);

  const arrived = useCallback((alert) => {
    setAlerts((list) => (list && list.some((a) => a.id === alert.id) ? list : [alert, ...(list || [])].slice(0, 100)));
    setUnread((n) => n + 1);
    playAlertSound();
    toast(`🔔 ${alert.title}`);
    // A new order: the alarm (on every page), Manage orders and the badges show it at once
    if (alert.type === "order") ordersChanged();
    // In the background without push: a browser notification (with push on, the service worker shows one)
    if (document.hidden && !pushOnRef.current && notificationsSupported() && Notification.permission === "granted") {
      const note = new Notification(alert.title, { body: alert.body, tag: `alert-${alert.id}`, icon: "/brand/icon-192.png" });
      note.onclick = () => { window.focus(); open(alert); note.close(); };
    }
  }, [open]);
  const arrivedRef = useRef(arrived);
  arrivedRef.current = arrived;

  // Sound can only play after the first click or key press on the page
  useEffect(() => {
    const unlock = () => unlockAlertSound();
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => { window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
  }, []);

  // Signed in: the alerts, the live stream (reconnecting after a drop, and catching up on what was missed), and push
  // notifications again if this browser already allowed them (for whoever is signed in now)
  const userId = user && user.id;
  useEffect(() => {
    if (!userId) {
      setAlerts(null);
      setUnread(0);
      currentPushSubscription().then((s) => s && s.unsubscribe()).catch(() => {}); // signed out: no more pushes here
      setPushOn(false);
      return undefined;
    }
    load();
    // Push again if this browser allowed notifications, also when it was asked just now at sign-in
    permissionAnswer().then((result) => {
      setPermission(result);
      if (result === "granted") enablePush().then(setPushOn).catch(() => setPushOn(false));
    });

    const controller = new AbortController();
    let attempt = 0;
    let timer = null;
    const connect = () => {
      readAlertStream((event, data) => {
        if (event === "ready") {
          attempt = 0;
          setUnread(data.unread);
        } else if (event === "alert") {
          arrivedRef.current(data);
        }
      }, controller.signal)
        .catch((err) => err)
        .then((err) => {
          if (controller.signal.aborted || (err && err.status === 401)) return;
          timer = setTimeout(() => { load(); connect(); }, RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)]);
          attempt += 1;
        });
    };
    connect();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [userId, load]);

  // Notifications allowed (or blocked) later in the browser's site settings: the panel follows without a reload
  useEffect(() => {
    if (!userId || !navigator.permissions || !navigator.permissions.query) return undefined;
    let status = null;
    const changed = () => {
      const now = notificationPermission();
      setPermission(now);
      if (now === "granted") enablePush().then(setPushOn).catch(() => setPushOn(false));
    };
    navigator.permissions.query({ name: "notifications" }).then((s) => { status = s; s.addEventListener("change", changed); }).catch(() => {});
    return () => { if (status) status.removeEventListener("change", changed); };
  }, [userId]);

  const turnOn = useCallback(async () => {
    const result = await turnOnNotifications();
    setPermission(result);
    setPushOn(Boolean(await currentPushSubscription().catch(() => null)));
    if (result === "granted") toast("Notifications are on.");
    else if (result === "denied") toast("Notifications are blocked. Allow them in your browser's site settings.", "warn");
  }, []);

  return (
    <AlertsContext.Provider value={{ alerts, unread, open, markAllRead, permission, pushOn, turnOn, reload: load }}>
      {children}
    </AlertsContext.Provider>
  );
}

const ICONS = { order: "📦", website: "🌐", limit: "⚠️" };

function timeAgo(date) {
  const seconds = Math.max(0, (Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  return new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

// The bell: the unread count stands out while there is any, and a click lists the alerts
export function AlertBell({ className = "" }) {
  const ctx = useAlerts();
  const [open, setOpen] = useState(false);
  if (!ctx) return null;
  const { alerts, unread, permission, pushOn, turnOn, markAllRead, reload } = ctx;
  const show = () => { setOpen(true); reload(); };
  const pick = (alert) => { setOpen(false); ctx.open(alert); };

  return (
    <>
      <button type="button" className={`alert-bell ${unread ? "has-unread" : ""} ${className}`} onClick={show} aria-label={unread ? `Alerts, ${unread} unread` : "Alerts"} title="Alerts">
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 0 0-5.25-6.78V3.5a1.75 1.75 0 0 0-3.5 0v.72A7 7 0 0 0 5 11v5l-1.7 1.7A1 1 0 0 0 4 19.4h16a1 1 0 0 0 .7-1.7Z" /></svg>
        {unread > 0 && <span className="alert-count">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {/* Rendered on the page itself: inside the sticky top bar the modal would be pinned to the bar */}
      {open && createPortal(
        <Modal eyebrow="ALERTS" title="Alerts" description={unread ? `${unread} unread` : "You're all caught up."} onClose={() => setOpen(false)}>
          <div className="alerts-actions">
            {!notificationsSupported() ? (
              <span className="alerts-note">This browser can't show notifications. Alerts still arrive here with a sound.</span>
            ) : permission === "granted" ? (
              <span className="alerts-note on">✓ Notifications are on{pushOn ? ", even when the admin panel is closed" : " while the admin panel is open"}</span>
            ) : permission === "denied" ? (
              <span className="alerts-note">Notifications are blocked. Allow them in this browser's site settings.</span>
            ) : (
              <button type="button" className="primary-btn" onClick={turnOn}>🔔 Turn on notifications</button>
            )}
            {unread > 0 && <button type="button" className="outline-btn" onClick={markAllRead}>Mark all as read</button>}
          </div>
          {alerts === null ? (
            <Loader label="Loading alerts" />
          ) : !alerts.length ? (
            <p className="alerts-empty">No alerts yet. New orders will show up here the moment they are placed.</p>
          ) : (
            <ul className="alerts-list">
              {alerts.map((alert) => (
                <li key={alert.id}>
                  <button type="button" className={alert.read ? "" : "unread"} onClick={() => pick(alert)}>
                    <span className="alerts-icon" aria-hidden="true">{ICONS[alert.type] || "🔔"}</span>
                    <span className="alerts-text">
                      <b>{alert.title}</b>
                      {alert.body && <small>{alert.body}</small>}
                      <em>{timeAgo(alert.createdAt)}</em>
                    </span>
                    {!alert.read && <span className="alerts-dot" aria-label="Unread" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Modal>,
        document.body
      )}
    </>
  );
}
